import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  archiveContentObject,
  batchArchiveContentObjects,
  batchPublishContentObjects,
  batchUnpublishContentObjects,
  getContentObjectReferences,
  publishContentObject,
  scheduleContentObject,
  unpublishContentObject,
  unscheduleContentObject,
} from '../lib/flotiq-client';
import { checkResponseStatus } from '../lib/flotiq-client/response-errors';
import { invalidateContentObjects } from '../lib/queryClientHelpers';

import {
  defaultButtonsProps,
  defaultTextRender,
  useModals,
} from '../contexts/ModalContext';

import { BoxWithArrowIcon, WarningTriangleRedIcon } from '../images/shapes';

import CascadeModalContent from '../pages/AddContentObject/CascadeModalContent/CascadeModalContent';
import useSelectedSpace from './useSelectedSpace';
import useToken from './useToken';

const useObjectStatusChange = (contentTypeName, canCascadePublish = false) => {
  const modal = useModals();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const jwt = useToken();
  const { space } = useSelectedSpace();

  const showConfirmationModal = useCallback(
    (action, count) =>
      modal({
        title: (
          <div className="inline-flex text-red font-bold text-3xl items-center">
            <WarningTriangleRedIcon className="h-5 mr-2.5" />
            {t('Global.Warning')}
          </div>
        ),
        content:
          action === 'archive'
            ? t('ObjectStatus.Confirmation.Archive.Content', {
                count: count,
              })
            : t('ObjectStatus.Confirmation.Unpublish.Content', {
                count: count,
              }),
        buttons: [
          {
            key: 'archive',
            label:
              action === 'archive'
                ? t('ObjectStatus.Confirmation.Archive.Confirm')
                : t('ObjectStatus.Confirmation.Unpublish.Confirm'),
            result: true,
            color: 'red',
            iconImage: <BoxWithArrowIcon className="w-3 min-w-3" />,
            iconPosition: 'start',
          },
          {
            ...defaultButtonsProps.cancel,
            label: t('Global.Cancel'),
            result: false,
          },
        ],
      }),
    [modal, t],
  );

  const showCascadeConfirmationModal = useCallback(
    (references) =>
      modal({
        title: defaultTextRender.simple(t(`ObjectStatus.CascadePublish.Title`)),
        content: !references ? (
          t(`ObjectStatus.CascadePublish.ReferencesError`)
        ) : (
          <CascadeModalContent references={references} />
        ),
        size: 'lg',
        contentAdditionalClasses: 'pt-0',
        buttons: [
          {
            key: 'cascade',
            color: 'blue',
            label: t(`ObjectStatus.CascadePublish.Cascade`),
            result: true,
          },
          {
            key: 'single',
            color: 'blueBordered',
            label: t(`ObjectStatus.CascadePublish.Single`),
            result: false,
          },
        ],
      }),
    [modal, t],
  );

  const isCascadePublish = useCallback(
    async (id) => {
      try {
        const { body: objectReferences, status: objectReferencesStatus } =
          await getContentObjectReferences(jwt, space, {
            objectType: contentTypeName,
            objectId: id,
          });

        checkResponseStatus(objectReferences, objectReferencesStatus);
        const cascadeReferences = objectReferences.filter(
          ({ status, draftPublic }) => draftPublic && status !== 'public',
        );

        if (cascadeReferences.length > 0) {
          return showCascadeConfirmationModal(cascadeReferences);
        }

        return false;
      } catch {
        return showCascadeConfirmationModal();
      }
    },
    [contentTypeName, jwt, showCascadeConfirmationModal, space],
  );

  const handleObjectStatus = useCallback(
    async ({ ids, action, values }) => {
      const count = ids.length;


      if (action === 'unpublish' || action === 'archive') {
        const result = await showConfirmationModal(action, count);
        if (!result) return;
      }

      if (['schedule', 'unschedule'].includes(action) && count > 1) {
        return;
      }

      const methods = {
        unpublish:
          count === 1 ? unpublishContentObject : batchUnpublishContentObjects,
        archive:
          count === 1 ? archiveContentObject : batchArchiveContentObjects,
        publish:
          count === 1 ? publishContentObject : batchPublishContentObjects,
        schedule: scheduleContentObject,
        unschedule: unscheduleContentObject,
      };

      const cascade =
        !canCascadePublish || action !== 'publish'
          ? false
          : await isCascadePublish(ids);

      if (typeof cascade !== 'boolean') return false;

      try {
        const apiMethod = methods[action];
        const { body, status } = await apiMethod(jwt, space, {
          objectType: contentTypeName,
          ...(values ? values : {}),
          ...(cascade ? { hydrate: 1 } : {}),
          ...(count === 1 ? { objectId: ids } : ids),
        });

        checkResponseStatus(body, status);
        toast.success(t('ObjectStatus.SuccessStatusUpdate'));
      } catch (error) {
        toast.error(error.message);
      }

      return cascade;
    },
    [
      canCascadePublish,
      contentTypeName,
      isCascadePublish,
      jwt,
      showConfirmationModal,
      space,
      t,
    ],
  );

  const afterMutate = useCallback(
    (isCascadePublish = false) =>
      isCascadePublish
        ? invalidateContentObjects(queryClient)
        : invalidateContentObjects(queryClient, contentTypeName),
    [contentTypeName, queryClient],
  );

  const { isPending, mutate } = useMutation({
    mutationFn: handleObjectStatus,
    onSettled: afterMutate,
  });

  return {
    handleObjectStatus: mutate,
    isPending,
  };
};

export default useObjectStatusChange;
